import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2, Trash2, ClipboardCheck, Calendar, FileText, Camera, Music, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { meApi, formatApiError } from "@/lib/api";
import { formatDateTimeLong } from "@/lib/date";
import { PageHeader } from "./_shared";

const CATEGORY_ICON = {
  validation: ClipboardCheck,
  rdv: Calendar,
  document: FileText,
  photobooth: Camera,
  contrat: FileText,
  playlist: Music,
  info: Info,
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    meApi.notifications(200).then((d) => setItems(d.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const markAllRead = async () => {
    setBusy(true);
    try { await meApi.notificationsMarkRead(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const delNotif = async (id) => {
    try { await meApi.notificationsDelete(id); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const unreadCount = items.filter((n) => !n.is_read).length;

  return (
    <div data-testid="ec-notifications">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          eyebrow="Mises à jour"
          title="Mes"
          italic="notifications."
          desc="L'historique complet des actions effectuées sur votre espace client."
        />
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={busy}
            data-testid="notif-page-mark-all"
            className="btn-ghost-gold inline-flex items-center gap-2 !py-2.5 !text-[11px] mt-2"
          >
            <CheckCircle2 size={13} strokeWidth={1.5} /> Tout marquer comme lu ({unreadCount})
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-white/55 italic flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 ? (
        <div className="card-luxe p-12 text-center" data-testid="notif-empty">
          <Bell size={40} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
          <p className="text-white/65 mb-2">Vous n&apos;avez pas encore reçu de notification.</p>
          <p className="text-white/45 text-sm">
            Vous serez informé ici (et par email) de toutes les mises à jour :
            nouveau document, template à valider, RDV planifié, photos ajoutées…
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="notif-page-list">
          {items.map((n) => {
            const Icon = CATEGORY_ICON[n.category] || Info;
            const Wrapper = n.link ? Link : "div";
            const wrapperProps = n.link ? { to: n.link } : {};
            return (
              <li key={n.id} className={`card-luxe p-5 flex items-start gap-4 ${!n.is_read ? "border-[#DCB457]/50 bg-[#DCB457]/[0.04]" : ""}`} data-testid={`notif-page-item-${n.id}`}>
                <Icon size={18} strokeWidth={1.3} className={`shrink-0 mt-1 ${!n.is_read ? "text-[#DCB457]" : "text-white/40"}`} />
                <div className="flex-1 min-w-0">
                  <Wrapper {...wrapperProps} className="block">
                    <p className={`font-serif-luxe text-lg ${!n.is_read ? "text-white" : "text-white/70"}`}>{n.title}</p>
                    {n.body && <p className="text-sm text-white/60 mt-1 leading-relaxed">{n.body}</p>}
                    <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-2">
                      {formatDateTimeLong(n.created_at) || n.created_at?.slice(0, 16).replace("T", " ")}
                      {!n.is_read && <span className="ml-3 text-[#DCB457]">● Non lu</span>}
                    </p>
                  </Wrapper>
                </div>
                <button
                  onClick={() => delNotif(n.id)}
                  data-testid={`notif-page-del-${n.id}`}
                  className="text-white/40 hover:text-red-400"
                  aria-label="Supprimer"
                >
                  <Trash2 size={14} strokeWidth={1.4} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
